import { type MouseEvent, useCallback, useState } from 'react';
import type { UseFieldArrayUpdate } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type {
  AudienceForm,
  AudienceListType,
  GroupFieldName,
  GroupsPath,
} from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { ConfirmationModal } from '../../../../ConfirmationModal.tsx';
import { useDemographicsSection } from '../../../hooks/useDemographicsSection.ts';
import { BaseFilterSection } from '../BaseFilterSection.tsx';
import { DemographicsExpanded } from './DemographicsExpanded.tsx';

type DemographicsSectionProps = {
  groupFieldName: GroupFieldName;
  listType: AudienceListType;
  update: UseFieldArrayUpdate<AudienceForm, GroupsPath>;
  index: number;
  isExpanded: boolean;
  isEdit: boolean;
  onToggleExpand: () => void;
};

export const DemographicsSection = ({
  groupFieldName,
  listType,
  update,
  index,
  isExpanded,
  isEdit,
  onToggleExpand,
}: DemographicsSectionProps) => {
  const { t } = useTranslation();
  const [showClearModal, setShowClearModal] = useState(false);

  const {
    clearAllDemographics,
    ageSegments,
    genderSegments,
    setDemographicAgeSegments,
    setDemographicGenderSegments,
    ageRanges,
    genderText,
    numberOfAgeAndGenderRangesSelected,
  } = useDemographicsSection({
    groupFieldName,
    update,
    index,
  });

  const handleClearButtonClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setShowClearModal(true);
  }, []);

  const handleClearConfirm = useCallback(() => {
    clearAllDemographics();
    setShowClearModal(false);
    onToggleExpand();
  }, [clearAllDemographics, onToggleExpand]);

  const handleClearCancel = useCallback(() => {
    setShowClearModal(false);
  }, []);

  const summaryText = `Age ranges: ${ageRanges}, Gender: ${genderText}`;

  return (
    <BaseFilterSection
      titleKey="Demographic"
      dataTestId="demographics-section"
      selectedCount={numberOfAgeAndGenderRangesSelected}
      showClearButton={isEdit && !!numberOfAgeAndGenderRangesSelected}
      onClear={handleClearButtonClick}
      summary={summaryText}
      expandableWhenEmpty
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
    >
      <DemographicsExpanded
        isEdit={isEdit}
        setDemographicAgeSegments={setDemographicAgeSegments}
        setDemographicGenderSegments={setDemographicGenderSegments}
        demographicGenderSegments={genderSegments}
        demographicAgeSegments={ageSegments}
        listType={listType}
      />

      <ConfirmationModal
        isOpen={showClearModal}
        onClose={handleClearCancel}
        onDelete={handleClearConfirm}
        title={t('Clear all demographic data')}
        item={t('All demographic data (age and gender)')}
      />
    </BaseFilterSection>
  );
};
